import React, { useState, useEffect, useCallback } from "react";
import { useObjectStore } from "../zustand/objects";
import { IconTrash, IconPlus, IconCopy, IconGripVertical } from '@tabler/icons-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import {editor} from "../styleFile.js"
import { getEditorChromeTheme } from "../utils/editorChromeTheme";

// MANTINE
import { Card, Input, Text, Group, Badge, ActionIcon } from '@mantine/core';
import { Screenshot } from "../components/Screenshot";
import debounce from 'lodash.debounce';

const SCENE_CARD_ACTIVE_BORDER = "#FE9A00";

const ArtboardPanel = ({ projectKey }) => {
  const {
    storyboardFields,
    artboards,
    removeArtboard,
    currentObjectArtboard,
    updateArtboardAndPreviewCamera,
    updateObjectSelected,
    addArtboardPositional,
    copyArtboardPositional,
    updateStoryboardFields,
    screenshotStatus,
    moveArtboard,
    setScreenshotStatus,
    arrangeUndoRedo,
    viewportThemeOverride,
  } = useObjectStore();
  const isDark = viewportThemeOverride === "dark";
  const chrome = getEditorChromeTheme(isDark);
  const sceneTheme = {
    heading: chrome.title,
    body: chrome.body,
    secondary: chrome.muted,
    border: chrome.border,
    buttonBorder: chrome.controlBorder,
    standardFill: chrome.bg,
    mutedFill: isDark ? "#333333" : "#F5F5F4",
    badgeText: chrome.body,
  };

  // const bringUsersHere = () => {
  //   bringToArtboard(currentObjectArtboard)
  // }

  const [artboardViewing, setArtboardViewing] = useState()
  const [editingName, setEditingName] = useState('')

  useEffect(() => {
    setArtboardViewing(currentObjectArtboard)
  }, [currentObjectArtboard])

  const cycleFunction = useCallback(async () => {
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const artboardKeys = Object.keys(artboards);

    for (let i = 0; i <= artboardKeys.length + 1; i++) {
      const val = artboardKeys[i];

      if (i <= artboardKeys.length) {
        setArtboardViewing(val);
      } else {
        setScreenshotStatus(false);
        setArtboardViewing(currentObjectArtboard);
      }

      await delay(2400);
    }
  }, [artboards, currentObjectArtboard, setScreenshotStatus]);


  useEffect(() => {
    screenshotStatus && cycleFunction()
  }, [cycleFunction, screenshotStatus])


  const handleChange = debounce((artboard, fieldType, value) => {
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "artboardUpdate",
      undo: false,
      projectKey: projectKey,
      artboard: artboard
    })
    // send update to zustand
    updateStoryboardFields(artboard, fieldType, value)

  }, 500)


  const handleSubmit = (e) => {
    e.preventDefault()
    e.target[0].blur()
  }

  const onDragEnd = (result) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return
    }
    // send current state to undoActions
    arrangeUndoRedo({
      updateType: "artboardMove",
      undo: false,
      projectKey: projectKey,
      artboard: currentObjectArtboard
    })
    // send update to zustand
    moveArtboard(source.index, destination.index)
  }

  const [snapping, setSnapping] = useState(null)

  const updateScreenshotArtboard = () => {
    snapping === artboards.length ? setSnapping(null) : setSnapping(snapping + 1)
  }

  return <>
    <div style={editor.artboardTopLevelContainer}>
      <div>
        <div style={{ paddingBottom: 0, marginBottom: 0 }}>
          <Group justify="space-between" align="center" wrap="nowrap" mb={20}>
            <Text
              fw={600}
              size={11}
              mb={0}
              tt="uppercase"
              style={{
                fontFamily: "Inter, sans-serif",
                color: sceneTheme.heading,
                letterSpacing: "0.55px",
                lineHeight: "16.5px",
              }}
            >
              All scenes
            </Text>
            <Badge
              size="sm"
              mb={0}
              variant="light"
              color="gray"
              styles={{
                root: {
                  backgroundColor: sceneTheme.mutedFill,
                  color: sceneTheme.badgeText,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  fontSize: 10,
                  lineHeight: "15px",
                  height: 19,
                  minHeight: 19,
                  paddingLeft: 8,
                  paddingRight: 8,
                  borderRadius: 4,
                  border: "none",
                },
              }}
            >
              {Object.keys(artboards).length}
            </Badge>
          </Group>
          <DragDropContext
            onDragEnd={onDragEnd}
          >
            <Droppable droppableId="artboard-list" direction="vertical">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {Object.keys(artboards).map((mapped, i) => (
                    <div key={i} >
                      {/* style={{ width: "auto" }} > */}
                      <Draggable key={i} index={i} draggableId={i.toString()}
                        >
                        {(provided) => (
                          <div {...provided.draggableProps} ref={provided.innerRef}>
                            <div key={i} style={{ marginLeft: 8, marginBottom: 8, paddingLeft: 0 }}>
                              <Card
                                withBorder={false}
                                shadow="none"
                                radius={4}
                                p={0}
                                style={{
                                  borderWidth: 2,
                                  borderStyle: "solid",
                                  borderColor:
                                    currentObjectArtboard === mapped
                                      ? SCENE_CARD_ACTIVE_BORDER
                                      : sceneTheme.border,
                                  backgroundColor:
                                    currentObjectArtboard === mapped
                                      ? sceneTheme.standardFill
                                      : sceneTheme.mutedFill,
                                  boxShadow: "none",
                                  overflow: "hidden",
                                }}
                              >

                                <Group wrap="nowrap" gap={0}>
                                  <div {...provided.dragHandleProps} style={editor.artboardCardContents}>
                                    <Text
                                      c={currentObjectArtboard === mapped ? sceneTheme.body : sceneTheme.secondary}
                                      style={{ display: "flex", alignItems: "center", paddingLeft: 6, paddingRight: 2 }}
                                    >
                                      <IconGripVertical size={18} />
                                    </Text>
                                  </div>
                                  <div
                                    style={{
                                      borderTopLeftRadius: 4,
                                      borderBottomLeftRadius: 4,
                                      overflow: "hidden",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {(artboardViewing === mapped) ? <div style={editor.artboardViewingScreenshot}>
                                      <Screenshot index={i} updateScreenshotArtboard={updateScreenshotArtboard} snapping={0} projectKey={projectKey} guestMode={false} artboard={mapped} /></div>
                                      :
                                      <div
                                        style={{
                                          ...editor.artboardNotViewingScreenshot,
                                          backgroundColor: sceneTheme.mutedFill,
                                        }}
                                        onClick={() => {
                                          updateObjectSelected('0')
                                          updateArtboardAndPreviewCamera(mapped);
                                          setArtboardViewing(mapped);
                                        }}
                                        size="small" />}
                                  </div>
                                  <div style={{ paddingLeft: 10, paddingRight: 8, paddingTop: 8, paddingBottom: 8, flex: 1, minWidth: 0 }}>
                                    {editingName === mapped ?
                                      <form onSubmit={(e) => handleSubmit(e)}>
                                        <Input
                                          size="xs"
                                          radius={4}
                                          styles={{
                                            input: {
                                              width: 85,
                                              fontFamily: "Inter, sans-serif",
                                              fontSize: 12,
                                              color: sceneTheme.body,
                                              borderColor: sceneTheme.border,
                                              backgroundColor: sceneTheme.standardFill,
                                            },
                                          }}
                                          type='text'
                                          autoFocus
                                          defaultValue={storyboardFields[mapped].name ? storyboardFields[mapped].name : 'Name'}
                                          onChange={(e) => handleChange(mapped, 'name', e.target.value)}
                                          onBlurCapture={() => setEditingName('')}

                                        />
                                        <Input type="submit" hidden />
                                      </form>
                                      :
                                      <Text
                                        fw={currentObjectArtboard === mapped ? 500 : 400}
                                        size="sm"
                                        style={{
                                          ...editor.artboardNameText,
                                          fontFamily: "Inter, sans-serif",
                                          color: currentObjectArtboard === mapped ? sceneTheme.body : sceneTheme.secondary,
                                          lineHeight: "20px",
                                          letterSpacing: "-0.15px",
                                          marginBottom: 8,
                                        }}
                                        onClick={() => {
                                          setEditingName(mapped)
                                        }}>
                                        {mapped}{storyboardFields[mapped].name ? ": " + storyboardFields[mapped].name : " Name"}
                                      </Text>}
                                    <Group gap={6} wrap="nowrap">
                                      <ActionIcon
                                        onClick={() => {
                                          // send current state to undoActions
                                          arrangeUndoRedo({
                                            updateType: "artboardAdd",
                                            undo: false,
                                            projectKey: projectKey,
                                            artboard: currentObjectArtboard
                                          })
                                          // send update to zustand
                                          addArtboardPositional(mapped)
                                        }}
                                        radius={8}
                                        size={22}
                                        variant="default"
                                        color="gray"
                                        style={{
                                          border: `1px solid ${sceneTheme.buttonBorder}`,
                                          backgroundColor: "transparent",
                                          color: currentObjectArtboard === mapped ? sceneTheme.body : sceneTheme.secondary,
                                          width: 22,
                                          height: 22,
                                          minWidth: 22,
                                        }}
                                      >
                                        <IconPlus size={14} />
                                      </ActionIcon>
                                      <ActionIcon
                                        onClick={() => {
                                           // send current state to undoActions
                                           arrangeUndoRedo({
                                            updateType: "artboardAdd",
                                            undo: false,
                                            projectKey: projectKey,
                                            artboard: currentObjectArtboard
                                          })
                                          // send update to zustand
                                          copyArtboardPositional(mapped);
                                        }}
                                        radius={8}
                                        size={22}
                                        variant="default"
                                        color="gray"
                                        style={{
                                          border: `1px solid ${sceneTheme.buttonBorder}`,
                                          backgroundColor: "transparent",
                                          color: currentObjectArtboard === mapped ? sceneTheme.body : sceneTheme.secondary,
                                          width: 22,
                                          height: 22,
                                          minWidth: 22,
                                        }}
                                      >
                                        <IconCopy size={14} />
                                      </ActionIcon>
                                      <ActionIcon
                                        disabled={currentObjectArtboard === mapped}
                                        onClick={() => {
                                           // send current state to undoActions
                                           arrangeUndoRedo({
                                            updateType: "artboardRemove",
                                            undo: false,
                                            projectKey: projectKey,
                                            artboard: currentObjectArtboard
                                          })
                                          removeArtboard(mapped, false)
                                        }}
                                        radius={8}
                                        size={22}
                                        variant="default"
                                        color="gray"
                                        style={{
                                          border: `1px solid ${sceneTheme.buttonBorder}`,
                                          backgroundColor: "transparent",
                                          color: currentObjectArtboard === mapped ? sceneTheme.body : sceneTheme.secondary,
                                          width: 22,
                                          height: 22,
                                          minWidth: 22,
                                        }}
                                      >
                                        <IconTrash size={14} />
                                      </ActionIcon>
                                    </Group>

                                  </div>
                                </Group>
                              </Card>
                            </div>
                          </div>)}
                      </Draggable></div>
                  ))}
                  {provided.placeholder}
                </div>

              )}
            </Droppable>
          </DragDropContext>
        </div></div>
    </div>
  </>;
};

export default ArtboardPanel;















